import { useTranslation } from 'react-i18next';
import { PanelLeftOpen } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useIsMobile } from '@/shared/hooks/use-mobile';
import { useProjectFilesView } from '../hooks/useProjectFilesView';
import { MoveFolderDialog } from './MoveFolderDialog';
import { MoveFileDialog } from './MoveFileDialog';
import { ProjectFilesSidebar } from './ProjectFilesSidebar';
import { ProjectFilesToolbar } from './ProjectFilesToolbar';
import { ProjectFilesList } from './ProjectFilesList';
import { SelectionBar } from './SelectionBar';
import { FolderFormDialog } from './FolderFormDialog';
import { FilePreviewOverlay } from './FilePreviewOverlay';
import { FileRenameDialog } from './FileRenameDialog';

interface ProjectFilesViewProps {
  projectId: string;
}

export function ProjectFilesView({ projectId }: ProjectFilesViewProps) {
  const { t } = useTranslation('projectFiles');
  const isMobile = useIsMobile();
  const {
    activeFolderId,
    setActiveFolderId,
    activeSection,
    setActiveSection,
    breadcrumbLabel,
    handleNavigateRoot,
    viewMode,
    setViewMode,
    search,
    setSearch,
    sortBy,
    setSortBy,
    sortDir,
    handleSortDirToggle,
    sidebarOpen,
    setSidebarOpen,
    folderDialog,
    setFolderDialog,
    fileDialog,
    setFileDialog,
    movingFolderId,
    setMovingFolderId,
    movingFileIds,
    setMovingFileIds,
    previewFile,
    setPreviewFile,
    selectedIds,
    setSelectedIds,
    folders,
    foldersLoading,
    filteredFiles,
    directFolders,
    filesLoading,
    sectionCounts,
    fileMutations,
    confirmOpen,
    confirmOptions,
    handleConfirm,
    handleCancel,
    handleDeleteFile,
    handleBulkDeleteFiles,
    handleUpload,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveFolder,
    handleMoveFolderConfirm,
    handleOpenRenameFile,
    handleRenameFileConfirm,
    handleToggleFavorite,
    handleToggleVisibility,
    handleSelect,
    handleMoveFileConfirm,
  } = useProjectFilesView(projectId);

  return (
    <div className="flex h-full overflow-hidden" data-testid="project-files-view">
      {isMobile && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-2 z-10 md:hidden"
              onClick={() => setSidebarOpen((v) => !v)}
              aria-label={t('sidebar.folders')}
            >
              <PanelLeftOpen className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('sidebar.folders')}</TooltipContent>
        </Tooltip>
      )}

      <div
        className={cn(
          'absolute inset-y-0 left-0 z-20 md:relative md:block',
          isMobile ? (sidebarOpen ? 'block' : 'hidden') : 'block',
        )}
      >
        <ProjectFilesSidebar
          folders={folders}
          isLoading={foldersLoading}
          activeFolderId={activeFolderId}
          activeSection={activeSection}
          counts={sectionCounts ?? { all: 0, recent: 0, favorites: 0, shared: 0, private: 0 }}
          onFolderSelect={(id) => {
            setActiveFolderId(id);
            if (isMobile) setSidebarOpen(false);
          }}
          onSectionSelect={setActiveSection}
          onRenameFolder={(id, name) =>
            setFolderDialog({ mode: 'rename', folderId: id, currentName: name })
          }
          onDeleteFolder={handleDeleteFolder}
          onMoveFolder={handleMoveFolder}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <ProjectFilesToolbar
          breadcrumbLabel={breadcrumbLabel}
          onNavigateRoot={handleNavigateRoot}
          isInsideFolder={activeFolderId !== null}
          search={search}
          onSearch={setSearch}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortByChange={setSortBy}
          onSortDirToggle={handleSortDirToggle}
          onUpload={handleUpload}
          onNewFolder={() => setFolderDialog({ mode: 'create' })}
          isUploading={fileMutations.upload.isPending}
        />

        {/* Wrapper always present to avoid layout shift on first selection */}
        <div className={selectedIds.size === 0 ? 'invisible' : undefined}>
          <SelectionBar
            count={selectedIds.size}
            onClear={() => setSelectedIds(new Set())}
            onMove={() => setMovingFileIds([...selectedIds])}
            onDelete={() => handleBulkDeleteFiles(selectedIds)}
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          <ProjectFilesList
            files={filteredFiles}
            folders={folders}
            directFolders={directFolders}
            isLoading={filesLoading}
            viewMode={viewMode}
            activeFolderId={activeFolderId}
            selectedIds={selectedIds}
            onDelete={handleDeleteFile}
            onMove={(id) => setMovingFileIds([id])}
            onRename={handleOpenRenameFile}
            onToggleFavorite={handleToggleFavorite}
            onToggleVisibility={handleToggleVisibility}
            onSelect={handleSelect}
            onPreview={setPreviewFile}
            onOpenFolder={setActiveFolderId}
            onRenameFolder={(id, name) =>
              setFolderDialog({ mode: 'rename', folderId: id, currentName: name })
            }
            onMoveFolder={handleMoveFolder}
            onDeleteFolder={handleDeleteFolder}
          />
        </div>
      </div>

      {folderDialog && (
        <FolderFormDialog
          open
          mode={folderDialog.mode}
          folders={folders}
          initialName={folderDialog.mode === 'rename' ? folderDialog.currentName : ''}
          onSubmitCreate={handleCreateFolder}
          onSubmitRename={handleRenameFolder}
          onCancel={() => setFolderDialog(null)}
        />
      )}

      <FileRenameDialog
        open={fileDialog?.mode === 'rename'}
        currentName={fileDialog?.mode === 'rename' ? fileDialog.currentName : ''}
        onSubmit={handleRenameFileConfirm}
        onCancel={() => setFileDialog(null)}
      />

      <MoveFolderDialog
        key={movingFolderId ?? ''}
        open={!!movingFolderId}
        folders={folders}
        onConfirm={handleMoveFolderConfirm}
        onCancel={() => setMovingFolderId(null)}
      />

      <MoveFileDialog
        key={movingFileIds.join(',')}
        open={movingFileIds.length > 0}
        folders={folders}
        onConfirm={handleMoveFileConfirm}
        onCancel={() => setMovingFileIds([])}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        title={confirmOptions?.title ?? ''}
        description={confirmOptions?.description}
        confirmLabel={confirmOptions?.confirmLabel}
        cancelLabel={confirmOptions?.cancelLabel}
        variant={confirmOptions?.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {previewFile && (
        <FilePreviewOverlay
          file={previewFile}
          files={filteredFiles}
          onClose={() => setPreviewFile(null)}
          onNavigate={setPreviewFile}
          onDelete={(id) => {
            handleDeleteFile(id);
            setPreviewFile(null);
          }}
          onMove={(id) => setMovingFileIds([id])}
        />
      )}
    </div>
  );
}
